import { ReactNode, useEffect, useState } from 'react';
import { ReverbContext } from '../contexts/ReverbContext.tsx';
import Echo from 'laravel-echo';
import Pusher, { Channel } from 'pusher-js';
import { useAppSelector } from '../stores/store.ts';
import { selectIsAuthenticated } from '../components/auth/authSelectors.ts';
import { useAccountFavoritesListener } from '../hooks/reverb-listeners/useAccountFavoritesListener.tsx';
import apiNet from '../newApi/apiNet.ts';
import { ChannelAuthorizationCallback } from 'pusher-js/src/core/auth/options.ts';
import { useAccountNotificationsListener } from '../hooks/reverb-listeners/useAccountNotificationsListener.tsx';
import { useAccountRemindersListener } from '../hooks/reverb-listeners/useAccountRemindersListener.tsx';

window.Pusher = Pusher;

interface IProps {
    children: ReactNode;
}

const VITE_REVERB_HOST = import.meta.env.VITE_REVERB_HOST || window.location.hostname;
const VITE_REVERB_PORT = import.meta.env.VITE_REVERB_PORT || 443;
const VITE_REVERB_SCHEME = import.meta.env.VITE_REVERB_SCHEME || 'https';
const VITE_REVERB_APP_KEY = import.meta.env.VITE_REVERB_APP_KEY;
const VITE_REVERB_AUTH_ENDPOINT = import.meta.env.VITE_REVERB_AUTH_ENDPOINT;

const ReverbProvider = ({ children }: IProps) => {
    const [echo, setEcho] = useState<Echo<'pusher'> | null>(null);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    useAccountFavoritesListener({ echo });
    useAccountNotificationsListener({ echo });
    useAccountRemindersListener({ echo });

    useEffect(() => {
        if (!isAuthenticated) return;
        const newEcho = new Echo({
            broadcaster: 'pusher',
            key: VITE_REVERB_APP_KEY,
            cluster: '',
            wsHost: VITE_REVERB_HOST,
            wsPort: VITE_REVERB_SCHEME === 'http' ? VITE_REVERB_PORT : 80,
            wssPort: VITE_REVERB_SCHEME === 'https' ? VITE_REVERB_PORT : 443,
            forceTLS: VITE_REVERB_SCHEME === 'https',
            enabledTransports: ['ws', 'wss'],
            authEndpoint: VITE_REVERB_AUTH_ENDPOINT,

            authorizer: (channel: Channel) => ({
                authorize: (socketId: string, callback: ChannelAuthorizationCallback) => {
                    apiNet.authReverb(
                        socketId,
                        channel.name,
                    )
                        .then((response) => {
                            callback(null, response.data);
                        })
                        .catch((error) => {
                            callback(error, null);
                        });
                },
            }),
        });
        setEcho(newEcho);
        return () => {
            if (newEcho) {
                newEcho.disconnect();
            }
        };
    }, [isAuthenticated]);


    return (
        <ReverbContext.Provider value={{ echo }}>
            {children}
        </ReverbContext.Provider>
    );
};

export default ReverbProvider;